import React, { useEffect, useState } from 'react';
import { Card, Spinner, Modal, Button } from 'react-bootstrap';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import axios from 'axios';
import '../../css/reportTypeSplitChart.css';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const ReportTypeSplitChart = () => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filterType, setFilterType] = useState('current'); // current, previous, custom
    const [reRenderComponent, setReRenderComponent] = useState(false);

    // Modal state
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    // Listen to storage changes
    window.addEventListener('storage', (e) => {
        setReRenderComponent(!reRenderComponent);
    });

    // Format date to API format: YYYY-MM-DD HH:mm:ss.SSS
    const formatDateForAPI = (dateString, isEndDate = false) => {
        if (isEndDate) {
            return `${dateString} 23:59:59.999`;
        }
        return `${dateString} 00:00:00.000`;
    };

    // Get first day of current month
    const getFirstDayOfCurrentMonth = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}-01`;
    };

    // Get current date
    const getCurrentDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Get first day of previous month
    const getFirstDayOfPreviousMonth = () => {
        const now = new Date();
        const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const month = now.getMonth() === 0 ? 12 : now.getMonth();
        return `${year}-${String(month).padStart(2, '0')}-01`;
    };

    // Get last day of previous month
    const getLastDayOfPreviousMonth = () => {
        const now = new Date();
        const firstDayCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDayPreviousMonth = new Date(firstDayCurrentMonth - 1);
        const year = lastDayPreviousMonth.getFullYear();
        const month = String(lastDayPreviousMonth.getMonth() + 1).padStart(2, '0');
        const day = String(lastDayPreviousMonth.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Initialize default dates
    useEffect(() => {
        const firstDay = getFirstDayOfCurrentMonth();
        const today = getCurrentDate();
        setStartDate(firstDay);
        setEndDate(today);
    }, []);

    // Fetch data from API
    const fetchReportTypeSplit = async (start, end) => {
        setLoading(true);
        setError(null);

        try {
            // Get ID token from localStorage - prioritize idTokenBilling for niroggyan.com APIs
            const idTokenBilling = localStorage.getItem('idTokenBilling');
            const loginDetails = JSON.parse(localStorage.getItem('loginDetails'));
            const idToken = idTokenBilling || loginDetails?.idToken || '';

            // Format dates for API
            const formattedStart = formatDateForAPI(start, false);
            const formattedEnd = formatDateForAPI(end, true);

            // Build API URL
            const apiUrl = `https://api.niroggyan.com/billing/smartreport/counts?query_on=report_type&query_by=range&start=${encodeURIComponent(formattedStart)}&end=${encodeURIComponent(formattedEnd)}`;

            console.log('Fetching Report Type Split:', apiUrl);

            // Make API call
            const config = {
                method: 'GET',
                url: apiUrl,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': idToken,
                },
            };

            const response = await axios(config);

            console.log('Report Type Split Response:', response.data);

            // Check if data is available
            if (response.data.isSuccess && response.data.data && response.data.data.length > 0) {
                processChartData(response.data.data);
            } else {
                console.warn('Report Type Split Data Issue:', response.data);
                setChartData(null);
                setError('no_data');
            }

            setLoading(false);
        } catch (err) {
            console.error('Error fetching report type split:', err);
            if (err.response) {
                console.error('Error Response Data:', err.response.data);
                console.error('Error Response Status:', err.response.status);
            }
            setError('api_error');
            setLoading(false);
        }
    };

    // Process data for pie chart
    const processChartData = (data) => {
        // Strict allowlist: only these 4 API keys are recognised and shown.
        // Any other label coming from the API (e.g. 'adva', 'tota', etc.) is silently ignored.
        const ALLOWED_TYPES = {
            historical: { label: 'Trend',    color: '#FF6384', hoverColor: '#FF4069' },
            dynamic:    { label: 'Personal', color: '#36A2EB', hoverColor: '#2E8FD6' },
            advanced:   { label: 'Total',    color: '#FFCE56', hoverColor: '#FFB93E' },
            compact:    { label: 'Compact',  color: '#4BC0C0', hoverColor: '#3AA8A8' },
        };

        // Keep only items whose report_type is in the allowlist
        const filteredData = data.filter(item => ALLOWED_TYPES[item.report_type]);

        // Calculate total count from allowed data only
        const totalCount = filteredData.reduce((sum, item) => sum + parseInt(item.count || 0), 0);

        // Prepare detailed data for list view
        const detailedData = filteredData.map((item) => {
            const count = parseInt(item.count || 0);
            const percentage = totalCount > 0 ? ((count / totalCount) * 100).toFixed(1) : 0;
            const { label, color, hoverColor } = ALLOWED_TYPES[item.report_type];
            return {
                label,
                count,
                percentage,
                color,
                hoverColor,
            };
        }).sort((a, b) => b.count - a.count); // Sort by count descending

        setChartData({
            labels: detailedData.map(d => d.label),
            datasets: [
                {
                    data: detailedData.map(d => d.count),
                    backgroundColor: detailedData.map(d => d.color),
                    hoverBackgroundColor: detailedData.map(d => d.hoverColor),
                    borderWidth: 2,
                    borderColor: '#fff',
                },
            ],
            details: detailedData, // Store for custom list
            total: totalCount,
        });
    };

    // Fetch data on component mount and when dates change
    useEffect(() => {
        if (startDate && endDate) {
            fetchReportTypeSplit(startDate, endDate);
        }
    }, [startDate, endDate, reRenderComponent]);

    // Handle filter selection
    const handleCurrentMonth = () => {
        const firstDay = getFirstDayOfCurrentMonth();
        const today = getCurrentDate();
        setStartDate(firstDay);
        setEndDate(today);
        setFilterType('current');
    };

    const handlePreviousMonth = () => {
        const firstDay = getFirstDayOfPreviousMonth();
        const lastDay = getLastDayOfPreviousMonth();
        setStartDate(firstDay);
        setEndDate(lastDay);
        setFilterType('previous');
    };

    const handleCustomMonth = () => {
        handleShow();
    };

    const applyCustomDates = () => {
        handleClose();
        setFilterType('custom');
        // Dates are already set via input onChange
    };

    // Chart options
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // Hide default legend
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.chart._metasets[context.datasetIndex].total;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return [` ${label}`, ` Count: ${value}`, ` Share: ${percentage}%`];
                    },
                },
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: { size: 14 },
                bodyFont: { size: 13 },
                displayColors: true,
            },
        },
    };

    // Custom Plugin to draw percentages on slices
    const percentagePlugin = {
        id: 'percentagePlugin',
        afterDatasetsDraw(chart, args, options) {
            const { ctx } = chart;

            chart.data.datasets.forEach((dataset, i) => {
                const meta = chart.getDatasetMeta(i);
                const total = dataset.data.reduce((acc, curr) => acc + curr, 0);

                meta.data.forEach((element, index) => {
                    if (element.hidden) return;

                    const value = dataset.data[index];
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';

                    // Only show labels for slices > 5% to avoid clutter
                    if ((value / total) > 0.05) {
                        const { x, y } = element.tooltipPosition();

                        ctx.save();
                        ctx.font = 'bold 12px Arial';
                        ctx.fillStyle = '#fff';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                        ctx.shadowBlur = 4;
                        ctx.fillText(percentage, x, y);
                        ctx.restore();
                    }
                });
            });
        }
    };

    // Render empty state
    const renderEmptyState = () => {
        if (error === 'no_data') {
            return (
                <div className="reportTypeSplitEmptyState">
                    <div className="emptyStateIcon">📊</div>
                    <h4>No Data Available</h4>
                    <p>There are no reports for the selected date range.</p>
                    <p className="emptyStateHint">Try selecting a different date range.</p>
                </div>
            );
        }

        if (error === 'api_error') {
            return (
                <div className="reportTypeSplitEmptyState">
                    <div className="emptyStateIcon">⚠️</div>
                    <h4>Unable to Load Data</h4>
                    <p>There was an error fetching the report type split data.</p>
                    <p className="emptyStateHint">Please try again later or contact support.</p>
                </div>
            );
        }

        return null;
    };




    return (
        <div className="mb-4">
            {/* Custom Date Modal */}
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title className="modalHeading">Select Custom Date Range</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <label className="labelTags" htmlFor="customStartDate">
                        Start Date
                    </label>
                    <input
                        id="customStartDate"
                        className="customDateTag"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                    <label className="labelTags" htmlFor="customEndDate">
                        End Date
                    </label>
                    <input
                        id="customEndDate"
                        className="customDateTag"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary btn-sm" onClick={applyCustomDates}>
                        Apply
                    </Button>
                    <Button variant="info btn-sm" onClick={handleClose}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Main Chart Card */}
            <Card>
                <Card.Body>
                    <Card.Title className="reportTypeSplitHeader">
                        <div style={{ fontWeight: 'bold' }}>
                            Report Type Distribution                        </div>
                        <div className="reportTypeSplitFilters">
                            <button
                                onClick={handleCurrentMonth}
                                className="metricsChartButtonCss"
                                style={{
                                    color: filterType === 'current' ? '#fff' : '',
                                    backgroundColor: filterType === 'current' ? '#3567D6' : '',
                                }}
                            >
                                Current
                            </button>
                            <button
                                onClick={handlePreviousMonth}
                                className="metricsChartButtonCss"
                                style={{
                                    color: filterType === 'previous' ? '#fff' : '',
                                    backgroundColor: filterType === 'previous' ? '#3567D6' : '',
                                }}
                            >
                                Previous
                            </button>
                            <button
                                onClick={handleCustomMonth}
                                className="metricsChartButtonCss"
                                style={{
                                    color: filterType === 'custom' ? '#fff' : '',
                                    backgroundColor: filterType === 'custom' ? '#3567D6' : '',
                                }}
                            >
                                Custom
                            </button>
                        </div>
                    </Card.Title>

                    {/* Chart or Empty State */}
                    <div className="reportTypeSplitContent">
                        {loading ? (
                            <div className="reportTypeSplitLoading">
                                <Spinner animation="border" role="status" />
                                <p>Loading chart data...</p>
                            </div>
                        ) : error ? (
                            renderEmptyState()
                        ) : chartData ? (
                            <div className="reportTypeSplitFlex">

                                {/* Left Side: Pie Chart */}
                                <div className="reportTypeSplitChartContainer">
                                    <Pie data={chartData} options={chartOptions} plugins={[percentagePlugin]} />
                                    <div className="chartTotalOverlay">
                                        <small>Total Reports</small>
                                        <div className="totalReportsCount">{chartData.total}</div>
                                    </div>
                                </div>

                                {/* Right Side: Progress Bars */}
                                <div className="reportTypeSplitList">
                                    {chartData.details.map((item, index) => (
                                        <div key={index} className="reportTypeListItem">
                                            <div className="reportTypeListHeader">
                                                <span className="reportTypeLabel" style={{ color: item.color, fontWeight: 'bold' }}>
                                                    {item.label}
                                                </span>
                                                <span className="reportTypeValue">
                                                    {item.count} <small style={{ color: '#888' }}>({item.percentage}%)</small>
                                                </span>
                                            </div>
                                            <div className="reportTypeProgressBarContainer">
                                                <div
                                                    className="reportTypeProgressBar"
                                                    style={{
                                                        width: `${item.percentage}%`,
                                                        backgroundColor: item.color
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                            </div>
                        ) : (
                            <div className="reportTypeSplitLoading">
                                <p>Initializing chart...</p>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default ReportTypeSplitChart;
